/**
 * ユーザーAPI（統合版）
 * ユーザー管理とプロファイル操作
 */

import apiClient from '@/shared/api/client'
import type {
    Profile,
    UserRole,
    GetUsersParams,
    CreateUserData,
    UserStats,
    UsersListResponse
} from '@/features/users/types'

/**
 * ユーザー一覧を取得
 */
export const getUsers = async (params?: GetUsersParams): Promise<UsersListResponse> => {
    try {
        const response = await apiClient.get<UsersListResponse>('/users', { params })
        return response.data
    } catch (error) {
        console.error('Failed to fetch users:', error)
        throw error
    }
}

/**
 * 現在のユーザーを取得
 */
export const getCurrentUser = async (): Promise<Profile> => {
    try {
        const response = await apiClient.get<Profile>('/users/me')
        return response.data
    } catch (error) {
        console.error('Failed to fetch current user:', error)
        throw error
    }
}

/**
 * 特定のユーザーを取得
 */
export const getUserById = async (userId: string): Promise<Profile> => {
    try {
        const response = await apiClient.get<Profile>(`/users/${userId}`)
        return response.data
    } catch (error) {
        console.error(`Failed to fetch user ${userId}:`, error)
        throw error
    }
}

/**
 * ユーザーを作成
 */
export const createUser = async (data: CreateUserData): Promise<Profile> => {
    try {
        const response = await apiClient.post<Profile>('/users', data)
        return response.data
    } catch (error) {
        console.error('Failed to create user:', error)
        throw error
    }
}

/**
 * ユーザーを更新
 */
export const updateUser = async (userId: string, data: Partial<Profile>): Promise<Profile> => {
    try {
        const response = await apiClient.put<Profile>(`/users/${userId}`, data)
        return response.data
    } catch (error) {
        console.error(`Failed to update user ${userId}:`, error)
        throw error
    }
}

/**
 * ユーザーの権限を更新
 */
export const updateUserRole = async (userId: string, role: UserRole): Promise<Profile> => {
    try {
        const response = await apiClient.put<Profile>(`/users/${userId}`, { role })
        return response.data
    } catch (error) {
        console.error(`Failed to update user role for ${userId}:`, error)
        throw error
    }
}

/**
 * ユーザーのアクティブ状態を更新
 */
export const updateUserActiveStatus = async (userId: string, isActive: boolean): Promise<Profile> => {
    try {
        const response = await apiClient.put<Profile>(`/users/${userId}`, { is_active: isActive })
        return response.data
    } catch (error) {
        console.error(`Failed to update user active status for ${userId}:`, error)
        throw error
    }
}

/**
 * ユーザーと職人の紐づけを更新
 */
export const updateUserCraftsmanLink = async (userId: string, craftsmanId: string | null): Promise<Profile> => {
    try {
        const response = await apiClient.put<Profile>(`/users/${userId}`, { craftsman_id: craftsmanId })
        return response.data
    } catch (error) {
        console.error(`Failed to update user craftsman link for ${userId}:`, error)
        throw error
    }
}

/**
 * ユーザーを削除（論理削除）
 */
export const deleteUser = async (userId: string): Promise<void> => {
    try {
        await apiClient.delete(`/users/${userId}`)
    } catch (error) {
        console.error(`Failed to delete user ${userId}:`, error)
        throw error
    }
}

/**
 * ユーザー統計を取得
 */
export const getUserStats = async (): Promise<UserStats> => {
    try {
        const response = await apiClient.get<UserStats>('/users/stats')
        return response.data
    } catch (error) {
        console.error('Failed to fetch user stats:', error)
        throw error
    }
}

/**
 * ユーザープロファイルを更新（後方互換性のため）
 */
export const updateUserProfile = async (
    userId: string,
    updates: Partial<Pick<Profile, 'role' | 'is_active' | 'craftsman_id'>>
): Promise<Profile> => {
    return updateUser(userId, updates)
}



